import { selectionLabel } from '@/components/opportunity/card-formatters';
import {
  ContextRow,
  HeadlineRow,
  PickRow,
  freshestPerBook,
} from '@/components/opportunity/opportunity-card-rows';
import { PerBookStrip } from '@/components/opportunity/per-book-grid';
import { Card } from '@/components/ui/card';
import { newIdempotencyKey } from '@/lib/idempotency';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import type { OpportunityRow } from '@tennis-scanner/shared';
import { toast } from 'sonner';

// Operator alert card — DENSE layout (architecture § 21.4.1).
// Target ≤ 130px so 5-6 alerts are visible per 1080p screen, matching
// commercial scanners (WinnerOdds-style table density). Three rows + an
// optional book strip:
//
//   1. context strip (state · tier · surface · tournament · ... · time)
//   2. match · edge · best book
//   3. pick · target · stake · actions
//   4. per-book strip (only when perBook has books)
//
// Sub-components live in opportunity-card-rows.tsx so this file stays under
// 300 LOC. Tooltips are mounted via TooltipProvider at the App root.

/** @deprecated read `opportunity.match` instead. Kept for prop compat. */
export interface MatchMeta {
  p1Name: string | null;
  p2Name: string | null;
  tournamentName: string | null;
  surface: string | null;
}

interface Props {
  opportunity: OpportunityRow;
  /** @deprecated ignored — match info now embedded on the opportunity row. */
  match?: MatchMeta;
  onExecuteClick: (opp: OpportunityRow) => void;
  onSelect?: (opp: OpportunityRow) => void;
}

const SNOOZE_MINUTES = 60; // [S] = pausa 60 min (V1 default)

export function OpportunityCard({ opportunity, onExecuteClick, onSelect }: Props) {
  const utils = trpc.useUtils();
  const isTerminal = ['closed', 'expired', 'executed', 'passed'].includes(opportunity.state);
  const match = opportunity.match;
  const sel = selectionLabel(opportunity.selection, match);

  const snooze = trpc.opportunities.snooze.useMutation({
    onSuccess: async () => {
      toast.success('Alerta pausada 1h');
      await utils.opportunities.list.invalidate();
    },
    onError: (err) => toast.error('No se pudo pausar', { description: err.message }),
  });

  const markPassed = trpc.opportunities.markPassed.useMutation({
    onSuccess: async () => {
      toast.success('Alerta descartada');
      await utils.opportunities.list.invalidate();
    },
    onError: (err) => toast.error('No se pudo descartar', { description: err.message }),
  });

  const handleSnooze = () => {
    snooze.mutate({
      opportunityId: opportunity.id,
      untilMinutes: SNOOZE_MINUTES,
      idempotencyKey: newIdempotencyKey(),
    });
  };

  const handlePass = () => {
    markPassed.mutate({
      opportunityId: opportunity.id,
      idempotencyKey: newIdempotencyKey(),
    });
  };

  const matchTitle =
    match?.p1Name && match?.p2Name
      ? `${match.p1Name} vs ${match.p2Name}`
      : `Match ${opportunity.canonicalMatchId.slice(0, 8)}`;
  const tournamentDisplay = match?.tournamentName ?? 'Torneo desconocido';
  const dataObservedAt = freshestPerBook(opportunity.perBook) ?? opportunity.lastEvaluatedAt;

  return (
    <Card
      data-slot="opportunity-card"
      className={cn(
        'group relative cursor-pointer px-3 py-2 transition-colors hover:bg-popover',
        isTerminal && 'opacity-60',
      )}
      onClick={() => onSelect?.(opportunity)}
    >
      <ContextRow
        opportunity={opportunity}
        tournamentDisplay={tournamentDisplay}
        dataObservedAt={dataObservedAt}
      />
      <HeadlineRow opportunity={opportunity} matchTitle={matchTitle} sel={sel} />
      <PickRow
        opportunity={opportunity}
        sel={sel}
        isTerminal={isTerminal}
        onExecuteClick={onExecuteClick}
        onSnooze={handleSnooze}
        onPass={handlePass}
        snoozePending={snooze.isPending}
        passPending={markPassed.isPending}
      />
      <PerBookStrip perBook={opportunity.perBook} targetOdds={opportunity.targetOdds} />
    </Card>
  );
}
